import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { FirebaseAuthService } from 'src/app/shared/providers/firebase-auth.service';
import { environment } from 'src/environments/environment';

// Where on the site the brief was written; goes to the team as debug data.
export interface ReferenceRequestContext {
  source: 'discover';
  // Collection the featured block was showing when the dialog opened, if any.
  collection?: string;
  // The search that found nothing (the empty state opens the dialog), if any.
  query?: string;
}

// New-reference requests from the landing. DELIVERY IS OFF (owner, 2026-09-18: nothing leaves
// the browser until the boss decides where requests should go) — submit() resolves without a
// network call and the dialog shows its "received" state. The wired-up path below is the one
// candidate: the team's feedback channel, POST /feedback, which mails FEEDBACK_RECIPIENTS with
// Reply-To = the user's email and stores a row (3dway-web-backend, internal/usecase/feedback.go);
// the "[Reference request]" prefix would tell the two apart in the inbox. Either way this one
// method is the seam for a real request queue (received / accepted / review / ready).
@Injectable({ providedIn: 'root' })
export class ReferenceRequestService {
  // Flip to true to send requests through /feedback. Frontend-only switch on purpose.
  private static readonly DELIVERY_ENABLED = false;
  private static readonly PREFIX = '[Reference request]';

  constructor(
    private readonly http: HttpClient,
    private readonly firebaseAuth: FirebaseAuthService,
  ) {}

  // Requires a signed-in user: the backend resolves the reply address from the uid.
  public async submit(brief: string, context: ReferenceRequestContext): Promise<void> {
    if (!ReferenceRequestService.DELIVERY_ENABLED) {
      // Nothing leaves the browser; the dialog still shows its "received" state.
      return;
    }

    const uid = await this.firebaseAuth.getUserUid();
    const token = await this.firebaseAuth.getUserToken();

    const body = {
      user_id: uid,
      text: `${ReferenceRequestService.PREFIX}\n\n${brief}`,
      debug_data: JSON.stringify({ kind: 'reference-request', ...context, page: window.location.pathname }),
    };
    const options = {
      headers: new HttpHeaders({ Accept: 'application/json', Authorization: `Bearer ${token}` }),
      withCredentials: true,
    };

    await lastValueFrom(this.http.post(`${environment.endpoints.webBackend}/feedback`, body, options));
  }
}
